import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { withdrawalsApi } from "@/api";
import type { WithdrawalFilter } from "@/api";
import { QUERY_KEYS } from "@/constants";

export const useWithdrawals = (filter: WithdrawalFilter) =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_WITHDRAWALS, filter],
    queryFn: () => withdrawalsApi.list(filter),
    placeholderData: keepPreviousData,
    select: res => ({ rows: res.data, meta: res.meta }),
  });

export const useWithdrawalSummary = () =>
  useQuery({
    queryKey: [QUERY_KEYS.GET_WITHDRAWAL_SUMMARY],
    queryFn: () => withdrawalsApi.summary(),
    select: res => res.data,
  });

const useInvalidateWithdrawals = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_WITHDRAWALS] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_WITHDRAWAL_SUMMARY] });
  };
};

export const useMarkWithdrawalPaid = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidateWithdrawals();
  return useMutation({
    mutationFn: (vars: { id: number; note?: string }) =>
      withdrawalsApi.markPaid(vars.id, vars.note),
    onSuccess: () => {
      invalidate();
      toast.success(t("WITHDRAWALS.PAID_TOAST"));
    },
  });
};

export const useRejectWithdrawal = () => {
  const { t } = useTranslation();
  const invalidate = useInvalidateWithdrawals();
  return useMutation({
    mutationFn: (vars: { id: number; reason: string }) =>
      withdrawalsApi.reject(vars.id, vars.reason),
    onSuccess: () => {
      invalidate();
      toast.success(t("WITHDRAWALS.REJECTED_TOAST"));
    },
  });
};
